// Runtime theming for the brand colors.
//
// The site has three brand colors, all exposed as CSS variables so the admin
// can change them at runtime without rebuilding:
//   * Primary  (--amf-primary)  → headers, hero, headings, deep-blue sections
//                                 (Tailwind amf-navy + shadcn --primary)
//   * Footer   (--amf-footer)   → the site footer background only
//                                 (Tailwind amf-footer; defaults to Primary)
//   * Accent   (--amf-accent)   → buttons, links, highlights
//                                 (Tailwind amf-burgundy / amf-gold + --accent/--ring)
// Each also gets a darker variant for hover states. Choices are persisted in
// the DB content system and applied on load, so every element using a color
// updates together.

export const DEFAULT_PRIMARY = "#173F7A"; // brand dark blue
export const DEFAULT_FOOTER = "#173F7A"; // brand dark blue (independent of Primary)
export const DEFAULT_ACCENT = "#F47B20"; // brand accent orange
export const PRIMARY_KEY = "theme.primary";
export const FOOTER_KEY = "theme.footer";
export const ACCENT_KEY = "theme.accent";
const PRIMARY_CACHE = "amf_primary";
const FOOTER_CACHE = "amf_footer";
const ACCENT_CACHE = "amf_accent";

// Curated palette (used for both color pickers).
export const PRESETS = [
  { name: "Dark Blue", hex: "#173F7A" },
  { name: "Logo Blue", hex: "#1EA5E3" },
  { name: "Accent Orange", hex: "#F47B20" },
  { name: "Royal Blue", hex: "#1D4ED8" },
  { name: "Sky Blue", hex: "#0EA5E9" },
  { name: "Deep Teal", hex: "#0E7490" },
];

// One-click schemes that set both colors at once.
export const SCHEMES = [
  { name: "Brand (Dark Blue + Orange)", primary: "#173F7A", accent: "#F47B20" },
  { name: "Logo Blue + Orange", primary: "#1EA5E3", accent: "#F47B20" },
  { name: "All Dark Blue", primary: "#173F7A", accent: "#173F7A" },
  { name: "Ocean (Dark Blue + Sky)", primary: "#173F7A", accent: "#0EA5E9" },
  { name: "Royal Blue", primary: "#1D4ED8", accent: "#1D4ED8" },
];

export const isValidHex = (hex) =>
  typeof hex === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex);

function hexToRgb(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const int = parseInt(h, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function rgbToHex(r, g, b) {
  const to = (v) =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

// "r g b" channel string for Tailwind's alpha-aware color utilities.
function rgbChannels(hex) {
  const { r, g, b } = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}

// Darken a hex color by a fraction (0..1) — used for hover states.
function darken(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

// Relative luminance (0..1) per WCAG, for contrast decisions.
function luminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

// Pick black or white text for the best contrast on a colored background.
export function readableText(hex) {
  if (!isValidHex(hex)) return "#FFFFFF";
  return luminance(hex) > 0.5 ? "#1F2937" : "#FFFFFF";
}

// Contrast ratio of white text on a colored background (1..21).
export function contrastWithWhite(hex) {
  if (!isValidHex(hex)) return 21;
  return 1.05 / (luminance(hex) + 0.05);
}

// Contrast ratio of a color against white *background* (for colored text).
function contrastOnWhite(hex) {
  return 1.05 / (luminance(hex) + 0.05);
}

// Darken a color until it reads acceptably as text on a white/light background.
function readableOnLight(hex) {
  let h = hex;
  let i = 0;
  while (contrastOnWhite(h) < 4.5 && i < 12) {
    h = darken(h, 0.1);
    i += 1;
  }
  return h;
}

// Convert hex to an "H S% L%" string for the shadcn HSL variables.
function hexToHsl(hex) {
  let { r, g, b } = hexToRgb(hex);
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// --- Live variable setters (no persistence; used for preview) ---
export function setPrimaryVars(hex) {
  if (!isValidHex(hex)) return;
  const root = document.documentElement;
  root.style.setProperty("--amf-primary", hex);
  root.style.setProperty("--amf-primary-rgb", rgbChannels(hex)); // Tailwind alpha
  root.style.setProperty("--amf-primary-dark", darken(hex, 0.18));
  root.style.setProperty("--amf-primary-fg", readableText(hex));
  root.style.setProperty("--primary", hexToHsl(hex)); // shadcn primary
}

export function setFooterVars(hex) {
  if (!isValidHex(hex)) return;
  const root = document.documentElement;
  root.style.setProperty("--amf-footer", hex);
  root.style.setProperty("--amf-footer-rgb", rgbChannels(hex)); // Tailwind alpha
  root.style.setProperty("--amf-footer-fg", readableText(hex));
}

export function setAccentVars(hex) {
  if (!isValidHex(hex)) return;
  const root = document.documentElement;
  root.style.setProperty("--amf-accent", hex);
  root.style.setProperty("--amf-accent-rgb", rgbChannels(hex)); // Tailwind alpha
  root.style.setProperty("--amf-accent-dark", darken(hex, 0.14));
  root.style.setProperty("--amf-accent-fg", readableText(hex));
  const text = readableOnLight(hex); // readable as text on light backgrounds
  root.style.setProperty("--amf-accent-text", text);
  root.style.setProperty("--amf-accent-text-rgb", rgbChannels(text));
  const hsl = hexToHsl(hex);
  root.style.setProperty("--accent", hsl); // shadcn accent
  root.style.setProperty("--ring", hsl); // focus rings
}

// --- Apply + persist locally (avoids a color flash on next load) ---
export function applyTheme({ primary, footer, accent } = {}) {
  if (primary !== undefined) {
    const v = isValidHex(primary) ? primary : DEFAULT_PRIMARY;
    setPrimaryVars(v);
    try { localStorage.setItem(PRIMARY_CACHE, v); } catch (e) { /* ignore */ }
  }
  if (footer !== undefined) {
    const v = isValidHex(footer) ? footer : DEFAULT_FOOTER;
    setFooterVars(v);
    try { localStorage.setItem(FOOTER_CACHE, v); } catch (e) { /* ignore */ }
  }
  if (accent !== undefined) {
    const v = isValidHex(accent) ? accent : DEFAULT_ACCENT;
    setAccentVars(v);
    try { localStorage.setItem(ACCENT_CACHE, v); } catch (e) { /* ignore */ }
  }
}

export function cachedTheme() {
  let primary = DEFAULT_PRIMARY;
  let footer = DEFAULT_FOOTER;
  let accent = DEFAULT_ACCENT;
  try {
    primary = localStorage.getItem(PRIMARY_CACHE) || DEFAULT_PRIMARY;
    footer = localStorage.getItem(FOOTER_CACHE) || DEFAULT_FOOTER;
    accent = localStorage.getItem(ACCENT_CACHE) || DEFAULT_ACCENT;
  } catch (e) {
    /* ignore */
  }
  return { primary, footer, accent };
}
